/**
 * InstrumentUsage.ts
 * Transient types for instrument usage scanning and smart delete operations
 * 
 * Contains interfaces for usage analysis results, delete confirmation states,
 * and related enums. These types support the UI and business logic without
 * being persisted to the database.
 */

// ===== ENUMS =====

/**
 * Types of usage sources that can reference an instrument
 */
export enum UsageSourceType {
  TRADE_EXECUTION = 'trade-execution',
  MARKET_STATE = 'market-state', 
  FEE_VERIFICATION = 'fee-verification'
}

/**
 * Recommended delete action based on usage analysis
 */
export enum DeleteAction {
  /** Can be permanently removed - no usage found */
  HARD_DELETE = 'hard-delete',
  /** Must be archived - usage found, deletion would break data integrity */
  ARCHIVE = 'archive'
}

/**
 * Status of a delete operation
 */
export enum DeleteOperationStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in-progress', 
  COMPLETED = 'completed',
  FAILED = 'failed'
}

// ===== USAGE DETAIL INTERFACES =====

/**
 * Individual usage reference with display information
 */
export interface UsageDetail {
  /** Unique identifier of the referencing record */
  id: string;
  
  /** User-friendly description of the usage */
  displayText: string;
  
  /** When this usage was created */
  createdAt: string;
  
  /** Additional context for display (optional) */
  metadata?: {
    /** Account name for trade executions */
    accountName?: string;
    /** Session date for market states */
    sessionDate?: string;
    /** Broker name for fee verifications */
    brokerName?: string;
  };
}

/**
 * Usage breakdown by source type
 */
export interface UsageBreakdown {
  /** Trade execution references */
  tradeExecutions: UsageDetail[];
  
  /** Market state references */
  marketStates: UsageDetail[];
  
  /** Fee verification references */
  feeVerifications: UsageDetail[];
}

// ===== MAIN RESULT INTERFACES =====

/**
 * Complete usage analysis result for a single instrument
 */
export interface InstrumentUsageResult {
  /** The instrument being analyzed */
  instrumentId: string;
  
  /** Trading symbol for display */
  instrumentSymbol: string;
  
  /** Display name for user identification */
  instrumentName: string;
  
  /** Template status for context (undefined if not applicable) */
  templateStatus?: 'up-to-date' | 'outdated' | 'custom' | undefined;
  
  /** Total usage count across all sources */
  totalUsageCount: number;
  
  /** Detailed breakdown by source type */
  usageBreakdown: UsageBreakdown;
  
  /** Computed recommended action */
  recommendedAction: DeleteAction;
  
  /** User-friendly explanation of recommendation */
  actionReason: string;
  
  /** Whether this instrument can be hard deleted */
  canHardDelete: boolean;
  
  /** Warning messages for user (if any) */
  warnings?: string[] | undefined;
}

/**
 * Batch usage analysis result for multiple instruments
 */
export interface BulkInstrumentUsageResult {
  /** Individual results for each scanned instrument */
  scannedInstruments: InstrumentUsageResult[];
  
  /** Summary statistics */
  summary: {
    /** Number of instruments that can be hard deleted */
    canHardDelete: number;
    
    /** Number of instruments that must be archived */
    mustArchive: number;
    
    /** Total instruments analyzed */
    totalScanned: number;
    
    /** Total usage references found */
    totalUsageReferences: number;
  };
  
  /** Whether this is a mixed operation (some archive, some hard delete) */
  isMixedOperation: boolean;
  
  /** Overall operation complexity for UX decisions */
  operationComplexity: 'simple' | 'moderate' | 'complex';
}

// ===== UI STATE INTERFACES =====

/**
 * Delete confirmation modal state
 */
export interface DeleteConfirmationState {
  /** Whether the modal is currently open */
  isOpen: boolean;
  
  /** Usage results to display in modal */
  instruments: InstrumentUsageResult[];
  
  /** Type of delete operation */
  operationType: 'single' | 'bulk';
  
  /** Whether operation is currently processing */
  isProcessing: boolean;
  
  /** Current processing step (for progress display) */
  processingStep?: string;
  
  /** Progress percentage (0-100) */
  progressPercentage?: number;
  
  /** Any error that occurred during processing */
  error?: string;
}

/**
 * Individual delete operation for batch processing
 */
export interface DeleteOperation {
  /** Instrument to delete */
  instrumentId: string;
  
  /** Action to take */
  action: DeleteAction;
  
  /** Current status */
  status: DeleteOperationStatus;
  
  /** Error message if failed */
  error?: string;
  
  /** Timestamp of completion */
  completedAt?: string;
}

/**
 * Batch delete operation state
 */
export interface BatchDeleteState {
  /** Individual operations */
  operations: DeleteOperation[];
  
  /** Overall batch status */
  overallStatus: DeleteOperationStatus;
  
  /** Success count */
  successCount: number;
  
  /** Failure count */
  failureCount: number;
  
  /** Start time */
  startedAt?: string;
  
  /** Completion time */
  completedAt?: string;
}

// ===== UTILITY TYPE GUARDS =====

/**
 * Type guard to check if usage result indicates hard delete is safe
 */
export const canSafelyHardDelete = (result: InstrumentUsageResult): boolean => {
  return result.totalUsageCount === 0 && result.recommendedAction === DeleteAction.HARD_DELETE;
};

/**
 * Type guard to check if bulk result contains mixed operations
 */
export const hasMixedOperations = (result: BulkInstrumentUsageResult): boolean => {
  return result.isMixedOperation;
};

/**
 * Get user-friendly description of delete action
 */
export const getDeleteActionDescription = (action: DeleteAction): string => {
  switch (action) {
    case DeleteAction.HARD_DELETE:
      return 'Permanently delete';
    case DeleteAction.ARCHIVE:
      return 'Archive (soft delete)';
    default:
      return 'Unknown action';
  }
};

/**
 * Get color theme for delete action (HeroUI compatible)
 */
export const getDeleteActionColor = (action: DeleteAction): 'danger' | 'warning' => {
  switch (action) {
    case DeleteAction.HARD_DELETE:
      return 'danger';
    case DeleteAction.ARCHIVE:
      return 'warning';
    default:
      return 'warning';
  }
};